import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AlertController } from '@ionic/angular';

export interface ErrorPopupOptions {
  primaryButtonText?: string;
  /** Navigate when the primary button is tapped. */
  navigateTo?: string;
  /** Run before navigation when the primary button is tapped. */
  onPrimaryAction?: () => void;
}

/**
 * Reusable centered error popup for auth and form flows.
 * Replaces danger toasters with the styled Ionic alert used on login.
 * Deduplicates concurrent/rapid duplicate messages to avoid popup stacking.
 */
@Injectable({
  providedIn: 'root',
})
export class ErrorPopupService {
  private activeAlert: HTMLIonAlertElement | null = null;
  private lastNormalizedMessage = '';
  private lastShownAt = 0;
  private sessionExpiredHandled = false;
  private readonly dedupeMs = 3000;

  constructor(
    private alertController: AlertController,
    private router: Router
  ) {}

  resolveErrorMessage(error: unknown, fallback = 'Something went wrong'): string {
    if (!error) {
      return fallback;
    }
    if (typeof error === 'string') {
      const text = error.trim();
      if (/^Http failure response for /i.test(text)) {
        return fallback;
      }
      return text || fallback;
    }
    const err = error as {
      error?: { message?: string; success?: boolean };
      message?: string;
      status?: number;
    };
    if (err?.error?.message) {
      return err.error.message;
    }
    const wrapper = err?.message?.trim() ?? '';
    if (wrapper && !/^Http failure response for /i.test(wrapper)) {
      return wrapper;
    }
    if (err?.status === 404) {
      return 'The requested resource was not found. Please try again or contact support.';
    }
    return fallback;
  }

  /** True after a 401 session-expiry alert was shown; suppresses follow-up API error popups. */
  isSessionExpired(): boolean {
    return this.sessionExpiredHandled;
  }

  /** Returns true only for the first session-expiry alert in a burst of 401s. */
  consumeSessionExpiredAlert(): boolean {
    if (this.sessionExpiredHandled) {
      return false;
    }
    this.sessionExpiredHandled = true;
    return true;
  }

  resetSessionState(): void {
    this.sessionExpiredHandled = false;
    this.lastNormalizedMessage = '';
    this.lastShownAt = 0;
  }

  private normalizeMessage(message: string): string {
    return (message || 'Something went wrong').trim().toLowerCase();
  }

  private shouldSuppress(message: string): boolean {
    const normalized = this.normalizeMessage(message);
    const now = Date.now();

    if (this.activeAlert) {
      return true;
    }
    if (
      normalized === this.lastNormalizedMessage &&
      now - this.lastShownAt < this.dedupeMs
    ) {
      return true;
    }
    return false;
  }

  async show(
    message: string,
    header = '',
    options?: ErrorPopupOptions
  ): Promise<void> {
    const text = (message || 'Something went wrong').trim();
    if (this.shouldSuppress(text)) {
      return;
    }

    this.lastNormalizedMessage = this.normalizeMessage(text);
    this.lastShownAt = Date.now();

    const buttons =
      options?.navigateTo || options?.primaryButtonText
        ? [
            {
              text: options.primaryButtonText ?? 'Buy',
              cssClass: 'error-alert-ok',
              handler: () => {
                options?.onPrimaryAction?.();
                if (options?.navigateTo) {
                  void this.router.navigateByUrl(options.navigateTo);
                }
              },
            },
          ]
        : [{ text: 'OK', cssClass: 'error-alert-ok', role: 'cancel' }];

    const alert = await this.alertController.create({
      ...(header ? { header } : {}),
      message: text,
      buttons,
      cssClass: 'error-alert-modal',
      mode: 'ios',
    });

    this.activeAlert = alert;
    alert.onDidDismiss().then(() => {
      if (this.activeAlert === alert) {
        this.activeAlert = null;
      }
    });

    await alert.present();
  }

  showFromError(error: unknown, fallback = 'Something went wrong'): void {
    if (this.sessionExpiredHandled) {
      return;
    }
    void this.show(this.resolveErrorMessage(error, fallback));
  }
}
